import { apiClient } from './api'

// ========================================
// Types & Interfaces
// ========================================

export type CreditAccount = {
  id: string
  reseller_id: string
  customer_id: string
  balance: number
  credit_limit: number
  currency: string
  status: string
  created_at: string
  updated_at: string
}

export type CreditTransaction = {
  id: string
  credit_account_id: string
  type: string // 'credit', 'debit', 'adjustment'
  amount: number
  balance_before: number
  balance_after: number
  description: string
  reference_type: string | null
  reference_id: string | null
  created_by: string | null
  created_at: string
}

export type CreditBalance = {
  balance: number
  credit_limit: number
  available_credit: number
  currency: string
  status: string
}

export type TransactionListResponse = {
  transactions: CreditTransaction[]
  total: number
  skip: number
  limit: number
}

// ========================================
// Request Schemas
// ========================================

export type CreateCreditAccountRequest = {
  currency?: string
  credit_limit?: number
}

export type AddCreditRequest = {
  amount: number
  description: string
  reference_type?: string
  reference_id?: string
}

export type DeductCreditRequest = {
  amount: number
  description: string
  reference_type?: string
  reference_id?: string
  allow_negative?: boolean
}

export type AdjustCreditRequest = {
  amount: number
  description: string
  reference_type?: string
  reference_id?: string
}

export type UpdateCreditLimitRequest = {
  credit_limit: number
}

// ========================================
// API Client
// ========================================

export const creditApi = {
  // ========================================
  // Credit Account Management
  // ========================================

  /**
   * Cria ou obtém conta de crédito para um cliente
   */
  createCreditAccount: (
    customerId: string,
    data: CreateCreditAccountRequest,
  ): Promise<CreditAccount> => {
    return apiClient.post(`/api/v1/reseller/customers/${customerId}/credit-account`, data)
  },

  /**
   * Remove (fecha) conta de crédito do cliente
   */
  deleteCreditAccount: (customerId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/reseller/customers/${customerId}/credit-account`)
  },

  // ========================================
  // Credit Operations
  // ========================================

  /**
   * Adiciona crédito à conta do cliente
   */
  addCredit: (customerId: string, data: AddCreditRequest): Promise<CreditTransaction> => {
    return apiClient.post(`/api/v1/reseller/customers/${customerId}/credit/add`, data)
  },

  /**
   * Deduz crédito da conta do cliente
   */
  deductCredit: (customerId: string, data: DeductCreditRequest): Promise<CreditTransaction> => {
    return apiClient.post(`/api/v1/reseller/customers/${customerId}/credit/deduct`, data)
  },

  /**
   * Ajusta crédito (positivo ou negativo)
   */
  adjustCredit: (customerId: string, data: AdjustCreditRequest): Promise<CreditTransaction> => {
    return apiClient.post(`/api/v1/reseller/customers/${customerId}/credit/adjust`, data)
  },

  // ========================================
  // Credit Limit Management
  // ========================================

  /**
   * Atualiza limite de crédito do cliente
   */
  updateCreditLimit: (
    customerId: string,
    data: UpdateCreditLimitRequest,
  ): Promise<CreditAccount> => {
    return apiClient.put(`/api/v1/reseller/customers/${customerId}/credit/limit`, data)
  },

  // ========================================
  // Balance and Transactions
  // ========================================

  /**
   * Consulta saldo de crédito do cliente
   */
  getCreditBalance: (customerId: string): Promise<CreditBalance> => {
    return apiClient.get(`/api/v1/reseller/customers/${customerId}/credit/balance`)
  },

  /**
   * Lista transações de crédito do cliente
   */
  listTransactions: (
    customerId: string,
    params?: {
      skip?: number
      limit?: number
    },
  ): Promise<TransactionListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const url = queryString
      ? `/api/v1/reseller/customers/${customerId}/credit/transactions?${queryString}`
      : `/api/v1/reseller/customers/${customerId}/credit/transactions`

    return apiClient.get(url)
  },

  // ========================================
  // Helper Functions
  // ========================================

  /**
   * Formata valores monetários
   */
  formatCurrency: (amount: number, currency: string = 'BRL'): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(amount)
  },

  /**
   * Retorna cor do badge de status
   */
  getStatusColor: (status: string): string => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      inactive: 'bg-gray-100 text-gray-800',
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  },

  /**
   * Retorna label do status
   */
  getStatusLabel: (status: string): string => {
    const labels: Record<string, string> = {
      active: 'Ativo',
      suspended: 'Suspenso',
      inactive: 'Inativo',
    }
    return labels[status] || status
  },

  /**
   * Retorna cor do tipo de transação
   */
  getTransactionTypeColor: (type: string): string => {
    const colors: Record<string, string> = {
      credit: 'text-green-600',
      debit: 'text-red-600',
      adjustment: 'text-blue-600',
    }
    return colors[type] || 'text-gray-600'
  },

  /**
   * Retorna label do tipo de transação
   */
  getTransactionTypeLabel: (type: string): string => {
    const labels: Record<string, string> = {
      credit: 'Crédito',
      debit: 'Débito',
      adjustment: 'Ajuste',
    }
    return labels[type] || type
  },
}

export default creditApi
